import { validateResponse, normalizeInput, requestConfig, finish } from "./google-oauth-repair.mjs";
import { z } from "zod";

export const CONTRACT_VERSION = 2 as const;
export const GOOGLE_CLIENT_ID = "618674730123-upnccahmpf5amp8ij1qcgdie3d11ch32.apps.googleusercontent.com";
export const GOOGLE_REDIRECT_URI = "https://app.itsbloxx.com/integrations/google/callback";
/** Scheduling and booking links: free/busy on the owner's calendars, and only the calendar Bloxx creates. */
export const CALENDAR_SCOPES = ["calendar.app.created", "calendar.freebusy"].map(scope => "https://www.googleapis.com/auth/" + scope);
/**
 * "Continue with Google" asks for identity only: enough to sign in or sign up. Analytics, Search
 * Console, Tag Manager and Calendar are each asked for separately, when the person connects that
 * product (Connections, Scheduling), so each can go through Google's verification on its own.
 * Xano auth/google/start (3822) requests exactly these, with online access.
 */
export const SIGN_IN_SCOPES = ["openid", "email", "profile"] as const;
export const BUSINESS_SCOPE = "https://www.googleapis.com/auth/business.manage";
export const FULL_SCOPES = ["openid", "email", "profile", ...[
  "analytics.readonly", "analytics.edit", "webmasters", "webmasters.readonly", "siteverification", "business.manage",
].map(scope => `https://www.googleapis.com/auth/${scope}`)].sort();
export const ModeSchema = z.enum(["initial", "upgrade", "recover"]);
export type GoogleOAuthMode = z.infer<typeof ModeSchema>;
const nonempty = z.string().refine(value => value.trim() === value && value.length > 0 && !["undefined", "null"].includes(value));
const state = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
// A 32-byte value has 43 base64url characters, with two unused bits in the final character.
const state32 = state.refine(value => "AEIMQUYcgkosw048".includes(value[value.length-1] || "!"));
const compatibleState = z.union([state32,z.uuid()]);
export const CallbackRequestSchema = z.strictObject({code:nonempty,state:compatibleState});
export const CancelRequestSchema = z.strictObject({state:compatibleState});
export const StartRequestSchema = z.strictObject({redirect_uri: z.literal(GOOGLE_REDIRECT_URI), mode: ModeSchema.optional()});
export const ERROR_MESSAGES = {
  GOOGLE_OAUTH_INVALID_REQUEST: "The Google connection request is invalid.",
  GOOGLE_OAUTH_AUTH_REQUIRED: "Sign in to Bloxx to connect Google.",
  GOOGLE_OAUTH_CONFIGURATION_INVALID: "Bloxx couldn’t prepare the Google connection. Please try again.",
  GOOGLE_OAUTH_START_UNAVAILABLE: "Google connection is temporarily unavailable. Please try again.",
} as const;
export const ERROR_STATUS = {GOOGLE_OAUTH_INVALID_REQUEST:400, GOOGLE_OAUTH_AUTH_REQUIRED:401, GOOGLE_OAUTH_CONFIGURATION_INVALID:500, GOOGLE_OAUTH_START_UNAVAILABLE:503} as const;
export type StartErrorCode = keyof typeof ERROR_MESSAGES;
const ErrorCodeSchema = z.enum(Object.keys(ERROR_MESSAGES) as [StartErrorCode, ...StartErrorCode[]]);
const SuccessShape = z.strictObject({
  schema_version:z.literal(2),ok:z.literal(true),request_id:z.uuid(),mode:ModeSchema,
  authorization_url:nonempty,client_id:z.literal(GOOGLE_CLIENT_ID),redirect_uri:z.literal(GOOGLE_REDIRECT_URI),
  state:state32,scopes:nonempty,access_type:z.literal("offline"),include_granted_scopes:z.literal(true),
  prompt:z.union([z.null(),z.literal("consent")]),
});
const FailureShape = z.strictObject({
  schema_version:z.literal(2),ok:z.literal(false),request_id:z.uuid(),
  error:z.strictObject({code:ErrorCodeSchema,message:z.string(),retryable:z.boolean()}),
});
export function scopeTokens(mode: GoogleOAuthMode): string[] { return mode === "upgrade" ? [BUSINESS_SCOPE] : [...FULL_SCOPES]; }
export const StartSuccessSchema = SuccessShape.superRefine((value,ctx) => {
  const issue = () => ctx.addIssue({code:"custom",message:"Invalid Google authorization configuration"});
  const expectedScopes = scopeTokens(value.mode).join(" ");
  if (value.scopes !== expectedScopes || !new Set(value.scopes.split(" ")).has(BUSINESS_SCOPE)) issue();
  if (value.prompt !== (value.mode === "recover" ? "consent" : null)) issue();
  let url: URL;
  try { url = new URL(value.authorization_url); } catch { issue(); return; }
  if (url.protocol !== "https:" || url.hostname !== "accounts.google.com" || url.port || url.username || url.password || url.hash || url.pathname !== "/o/oauth2/v2/auth") issue();
  const expected:Record<string,string> = {client_id:value.client_id,redirect_uri:value.redirect_uri,response_type:"code",state:value.state,scope:value.scopes,access_type:"offline",include_granted_scopes:"true"};
  if (value.prompt !== null) expected.prompt=value.prompt;
  if ([...url.searchParams.keys()].some(key=>!Object.prototype.hasOwnProperty.call(expected,key))) issue();
  for (const [key,wanted] of Object.entries(expected)) if (url.searchParams.getAll(key).length !== 1 || url.searchParams.get(key) !== wanted) issue();
});
export const StartFailureSchema = FailureShape.superRefine((value,ctx) => {
  if (value.error.message !== ERROR_MESSAGES[value.error.code] || value.error.retryable !== (value.error.code === "GOOGLE_OAUTH_START_UNAVAILABLE")) ctx.addIssue({code:"custom",message:"Invalid OAuth failure response"});
});
export const StartResponseSchema = z.union([StartSuccessSchema,StartFailureSchema]);
export type StartSuccess = Omit<z.infer<typeof StartSuccessSchema>, "schema_version"> & {schema_version:2|3;products?:string[];requested_scopes?:string[]};
export type StartFailure = z.infer<typeof StartFailureSchema>;
export type StartRequest = z.infer<typeof StartRequestSchema>;
export function failure(code:StartErrorCode,requestId:string):StartFailure {
  return StartFailureSchema.parse({schema_version:2,ok:false,request_id:requestId,error:{code,message:ERROR_MESSAGES[code],retryable:code==="GOOGLE_OAUTH_START_UNAVAILABLE"}});
}
export function prepareStart(rawRequest:unknown,context:{clientId:unknown;hasActiveConnection:boolean;state:string;requestId:string}):StartSuccess {
  const request=StartRequestSchema.parse(rawRequest);
  if(context.clientId!==GOOGLE_CLIENT_ID) throw new Error("GOOGLE_OAUTH_CONFIGURATION_INVALID");
  const mode=request.mode ?? (context.hasActiveConnection?"upgrade":"initial");
  const scopes=scopeTokens(mode).join(" ");const prompt=mode==="recover"?"consent":null;
  const url=new URL("https://accounts.google.com/o/oauth2/v2/auth");
  for(const [key,value] of Object.entries({client_id:GOOGLE_CLIENT_ID,redirect_uri:request.redirect_uri,response_type:"code",state:context.state,scope:scopes,access_type:"offline",include_granted_scopes:"true"}))url.searchParams.set(key,value);
  if(prompt)url.searchParams.set("prompt",prompt);
  return StartSuccessSchema.parse({schema_version:2,ok:true,request_id:context.requestId,mode,authorization_url:url.toString(),client_id:GOOGLE_CLIENT_ID,redirect_uri:request.redirect_uri,state:context.state,scopes,access_type:"offline",include_granted_scopes:true,prompt});
}
export function parseNavigation(payload:unknown):StartSuccess {
  const parsed=StartResponseSchema.safeParse(payload);
  if(!parsed.success)throw new Error("GOOGLE_OAUTH_RESPONSE_INVALID");
  if(parsed.data.ok === false)throw new Error(parsed.data.error.code);
  return parsed.data;
}

export const TelemetrySchema = z.strictObject({
  event:z.enum(["oauth_start_failed","oauth_navigation_prevented","oauth_callback_failed","google_discovery_completed"]),
  request_id:z.uuid(),release:z.string().regex(/^[a-zA-Z0-9._-]{1,80}$/),cohort:z.enum(["owner","legacy","qa"]),
  stage:z.enum(["start","navigation","callback","discovery"]),
  code:z.string().regex(/^[A-Z][A-Z0-9_]{1,79}$/),duration_ms:z.number().int().nonnegative(),http_status:z.number().int().min(0).max(599),
  accounts:z.number().int().nonnegative().optional(),locations:z.number().int().nonnegative().optional(),
});
export type OAuthTelemetry = z.infer<typeof TelemetrySchema>;

export const ClientTelemetrySchema = TelemetrySchema.omit({cohort:true});
export type OAuthClientTelemetry = z.infer<typeof ClientTelemetrySchema>;
export class GoogleOAuthResponseError extends Error {
  constructor(public code:string,public requestId:string,public httpStatus:number) {super(code);}
}
export function parseStartHttpResponse(payload:unknown,httpStatus:number):StartSuccess {
  if(payload && typeof payload === "object" && !Array.isArray(payload)) {
    const r=payload as Record<string,unknown>;
    if(r.schema_version === 3) {
      if(httpStatus===200 && validateResponse(r)) return r as unknown as StartSuccess;
      throw new GoogleOAuthResponseError("GOOGLE_OAUTH_RESPONSE_INVALID",crypto.randomUUID(),httpStatus);
    }
    if(!Object.prototype.hasOwnProperty.call(r,"schema_version") && !Object.prototype.hasOwnProperty.call(r,"authorization_url") && !Object.prototype.hasOwnProperty.call(r,"ok") && httpStatus===200) {
      try {
        const input=normalizeInput({redirect_uri:GOOGLE_REDIRECT_URI,mode:r.mode,products:r.products},false);
        const config=requestConfig(input);
        if(r.client_id!==GOOGLE_CLIENT_ID || typeof r.state!=="string" || !z.uuid().safeParse(r.state).success) throw Error();
        for(const k of ["scopes","requested_scopes","mode","prompt","access_type","include_granted_scopes"]) {
          if(JSON.stringify(r[k])!==JSON.stringify(config[k])) throw Error();
        }
        const keys=["scopes","requested_scopes","mode","prompt","access_type","include_granted_scopes","login_hint","state","client_id","products"];
        if(Object.keys(r).some(k=>!keys.includes(k))) throw Error();
        return finish(config,r.state,crypto.randomUUID()) as unknown as StartSuccess;
      } catch { throw new GoogleOAuthResponseError("GOOGLE_OAUTH_RESPONSE_INVALID",crypto.randomUUID(),httpStatus); }
    }
    if(r.ok===false && httpStatus!==200) {
      const error=r.error as Record<string,unknown>|undefined;
      throw new GoogleOAuthResponseError(typeof error?.code==="string" && Object.prototype.hasOwnProperty.call(ERROR_STATUS,error.code)?error.code:"GOOGLE_OAUTH_RESPONSE_INVALID",crypto.randomUUID(),httpStatus);
    }
  }
  const parsed=StartResponseSchema.safeParse(payload);
  if(!parsed.success)throw new GoogleOAuthResponseError("GOOGLE_OAUTH_RESPONSE_INVALID",crypto.randomUUID(),httpStatus);
  if(parsed.data.ok===false){
    if(httpStatus!==ERROR_STATUS[parsed.data.error.code])throw new GoogleOAuthResponseError("GOOGLE_OAUTH_RESPONSE_INVALID",parsed.data.request_id,httpStatus);
    throw new GoogleOAuthResponseError(parsed.data.error.code,parsed.data.request_id,httpStatus);
  }
  if(httpStatus!==200)throw new GoogleOAuthResponseError("GOOGLE_OAUTH_RESPONSE_INVALID",parsed.data.request_id,httpStatus);
  return parsed.data;
}
